import { ArrowRight } from '@orgus/ui/icons';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { Button, Drawer, LogViewer, Pressable, RelativeTime, RowStatus, StatusPill, cn, formatClock, formatDuration, formatInt, pipelineItemRowStatus, useNow } from '@orgus/ui';
import type { PillStatus } from '@orgus/ui';
import { FactGrid } from '../detail/FactGrid.tsx';
import { Fold, ProblemBanner, StageStrip, Timeline, sentence } from '../landing/LandingDrawer.tsx';
import type { StripOutcome, TimelineEntry } from '../landing/LandingDrawer.tsx';
import { stageTitle } from './model.ts';
import { attemptOf, pipelineChains } from './chains.ts';
import type { Chain } from './chains.ts';
import { useItemLog } from './useItemLog.ts';
import { commitTitle, failedLabel, failureSummary, finishedNote, humanTitle } from './present.ts';

const ITEM_PILL: Record<PipelineItem['state'], PillStatus> = { queued: 'queued', active: 'working', passed: 'done', failed: 'failed', cancelled: 'ended' };
const ITEM_STRIP: Record<PipelineItem['state'], StripOutcome> = { queued: 'queued', active: 'running', passed: 'passed', failed: 'failed', cancelled: 'cancelled' };

/** The header pill's word, one per stage as the rows say it: "Verify failed", "Verifying", "Landed", "Cancelled". */
export function itemPillLabel(item: Pick<PipelineItem, 'state' | 'stage'>, p: Pipeline): string {
  const stage = stageTitle(p, item.stage);
  switch (item.state) {
    case 'failed':
      return failedLabel(stage);
    case 'active':
      return stage;
    case 'passed':
      return p.stages.at(-1)?.title ?? 'Passed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Queued';
  }
}

/**
 * What happened to an item, oldest first, for the drawer's timeline: when it was submitted, when
 * it entered the stage it is in (or ended in), and how it ended. A pipeline reports no more than
 * that, so nothing is made up.
 */
export function itemTimeline(item: Pick<PipelineItem, 'state' | 'stage' | 'detail' | 'by' | 'startedAt' | 'stageStartedAt' | 'endedAt'>, p: Pipeline): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  const stage = stageTitle(p, item.stage);
  if (item.startedAt != null) out.push({ tone: 'info', text: item.by ? `Submitted by ${item.by}` : 'Submitted', at: item.startedAt });
  if (item.stageStartedAt != null && item.stageStartedAt !== item.startedAt && item.state !== 'queued') out.push({ tone: 'info', text: `${stage} started`, at: item.stageStartedAt });
  if (item.endedAt != null) {
    if (item.state === 'failed') {
      const reason = failureSummary(item, p).reason;
      out.push({ tone: 'error', text: reason ? `${failedLabel(stage)}, ${reason.charAt(0).toLowerCase()}${reason.slice(1)}` : failedLabel(stage), at: item.endedAt });
    } else if (item.state === 'passed') out.push({ tone: 'ok', text: p.stages.at(-1)?.title ?? 'Passed', at: item.endedAt });
    else if (item.state === 'cancelled') out.push({ tone: 'info', text: 'Cancelled', at: item.endedAt });
  }
  return out;
}

/** A failed item's banner: the failure, then what the pipeline said, as a sentence. */
export function itemProblem(item: Pick<PipelineItem, 'state' | 'stage' | 'detail'>, p: Pipeline): { title: string; lines: string[] } | null {
  if (item.state !== 'failed') return null;
  const note = finishedNote({ state: item.state, detail: item.detail });
  return { title: failedLabel(stageTitle(p, item.stage)), lines: [note ? sentence(note) : 'The pipeline did not say why.'] };
}

/** The attempts of one piece of work, oldest first, one line each: no separators to wrap, the one open marked. */
function Attempts({ chain, pipeline, activeId, onOpen }: { chain: Chain; pipeline: Pipeline; activeId: string | null; onOpen?: (i: PipelineItem) => void }) {
  return (
    <ol className="flex flex-col" aria-label="Attempts, oldest first">
      {chain.attempts.map((a, i) => {
        const st = pipelineItemRowStatus(a.state);
        const label = a.state === 'failed' ? 'Rejected' : a.state === 'passed' ? 'Landed' : st.label;
        const at = a.endedAt ?? a.startedAt;
        const here = a.id === activeId;
        return (
          <li key={a.id}>
            <Pressable
              onClick={() => !here && onOpen?.(a)}
              aria-current={here || undefined}
              className={cn('flex h-row items-center gap-3 rounded-control px-row-x text-ui', here ? 'selected-tint' : 'hover:bg-hover')}
            >
              <span className="w-4 shrink-0 text-right text-caption text-fg-subtle num">{formatInt(i + 1)}</span>
              <RowStatus {...st} label={label} />
              <span className="min-w-0 flex-1 truncate text-caption text-fg-subtle" title={a.detail}>
                {a.state === 'failed' && a.detail ? failureSummary(a, pipeline).reason : ''}
              </span>
              {at != null && <span className="shrink-0 text-caption text-fg-subtle num">{formatClock(at)}</span>}
            </Pressable>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One item in the side panel, in the landing drawer's anatomy: its status pill, why it failed in
 * a short banner, the stage strip, the labelled facts, its log (live while in flight; a log that
 * ended empty is not drawn as a box), then Attempts, Details and Timeline folded. When a newer
 * attempt of the same work exists, the footer opens it, so a failed attempt never reads as the
 * end of the story. The board stays in view beside it; Esc closes it.
 */
export function LogDialog({ pipeline, item, onClose, onOpenItem }: { pipeline: Pipeline | undefined; item: PipelineItem | undefined; onClose: () => void; onOpenItem?: (i: PipelineItem) => void }) {
  const now = useNow(1000);
  const open = !!item;
  // Its work's other attempts (rejected, resubmitted), when there are any.
  const attempt = pipeline && item ? attemptOf(pipelineChains(pipeline), item.id) : null;
  const attempts = attempt && attempt.chain.attempts.length > 1 ? attempt : null;
  const latest = attempts && attempts.n < attempts.chain.attempts.length ? (attempts.chain.attempts.at(-1) ?? null) : null;
  const log = useItemLog(pipeline, item, open);
  const title = item ? humanTitle(item) : null;
  const commit = title ? commitTitle(title.title) : null;
  const problem = item && pipeline ? itemProblem(item, pipeline) : null;
  const note = item && item.state !== 'failed' ? finishedNote(item) : null;
  const running = item?.state === 'active';
  const hasLog = log.text.trim().length > 0 || log.live || log.loading || log.error != null;
  const footer =
    latest && attempts && onOpenItem ? (
      <>
        <span className="flex-1" />
        <Button variant="primary" icon={<ArrowRight />} onClick={() => onOpenItem(latest)}>
          Open attempt {formatInt(attempts.chain.attempts.length)}
        </Button>
      </>
    ) : null;
  return (
    <Drawer
      open={open}
      onOpenChange={(v) => !v && onClose()}
      width={640}
      title={commit?.subject ?? 'Log'}
      eyebrow={
        item && pipeline ? (
          <>
            <StatusPill status={ITEM_PILL[item.state]} label={itemPillLabel(item, pipeline)} />
            {title?.id && <span className="font-mono text-caption text-fg-subtle">{title.id}</span>}
          </>
        ) : undefined
      }
      description={
        item && pipeline ? (
          <span className="flex flex-wrap items-center gap-x-2">
            {commit?.type && <span className="font-mono">{commit.type}</span>}
            <span>{pipeline.title}</span>
          </span>
        ) : undefined
      }
      footer={footer}
      bodyClassName="flex flex-col gap-6 px-6 py-4"
    >
      {item && pipeline && (
        <>
          {problem && <ProblemBanner tone="danger" title={problem.title} lines={problem.lines} />}
          <StageStrip stages={pipeline.stages} current={item.stage} outcome={ITEM_STRIP[item.state]} currentLabel={itemPillLabel(item, pipeline)} />
          <FactGrid
            label="Item facts"
            className="shrink-0"
            lines={[
              [
                { label: 'By', value: item.by ?? null, title: item.by },
                { label: 'Submitted', value: item.startedAt != null ? <RelativeTime ts={item.startedAt} /> : null },
                { label: running ? 'Running for' : 'Took', value: item.startedAt != null ? formatDuration(Math.max(0, (item.endedAt ?? now) - item.startedAt)) : null },
                { label: 'Attempt', value: attempts ? `${formatInt(attempts.n)} of ${formatInt(attempts.chain.attempts.length)}` : null },
              ],
            ]}
          />

          {item.logRef && hasLog && (
            <section aria-labelledby="item-log" className="flex shrink-0 flex-col">
              <h3 id="item-log" className="mb-2 text-caption font-medium text-fg-muted">
                Log
              </h3>
              <LogViewer
                label={`Log for ${item.title}`}
                text={log.text}
                live={log.live}
                loading={log.loading}
                error={log.error}
                onRetry={log.retry}
                emptyText={item.state === 'queued' ? 'Queued. Output appears when it starts.' : 'No output yet.'}
                className="h-72"
              />
            </section>
          )}

          {attempts && (
            <Fold title="Attempts" aside={`${formatInt(attempts.n)} of ${formatInt(attempts.chain.attempts.length)}`}>
              <Attempts chain={attempts.chain} pipeline={pipeline} activeId={item.id} onOpen={onOpenItem} />
            </Fold>
          )}

          <Fold title="Details">
            <FactGrid
              lines={[
                [
                  { label: 'Stage', value: stageTitle(pipeline, item.stage) },
                  { label: 'Ended', value: item.endedAt != null ? <RelativeTime ts={item.endedAt} /> : null },
                  { label: 'Log', value: item.logRef ? 'Kept' : 'Not kept' },
                ],
                [{ label: 'Note', value: note, title: note ?? undefined }],
              ]}
            />
          </Fold>

          <Timeline events={itemTimeline(item, pipeline)} />
        </>
      )}
    </Drawer>
  );
}
